import {
  SHAPES,
  CANVAS_ENTITY_EVENT,
  TRIPOD_ARROW_MIN_LENGTH,
  TRIPOD_ARROW_MIN_LENGTH_FACTOR,
} from '../const'
import type {
  CanvasEngineExportEntityType,
  ICanvasEngineEntityController,
  ShapeCreateConfigs,
  Vector2d,
} from '../types'
import { BaseSelectionHandler } from './base/baseSelectionHandler'
import { isApp } from '@/index'
import type { ILine, INode, IPointerEvent } from '../backend/IRenderer'
import { isEllipseNode, isLineNode } from '../backend/nodeGuards'
import { updateDirectionElements } from './types/direction/stateManager/helper'
import type { SelectionStrategy } from './base/selectionStrategy'
import { PointSelectionHandler } from './types/point/pointSelectionHandler'
import { DirectionSelectionHandler } from './types/direction/directionSelectionHandler'
import { RectSelectionHandler } from './types/rect/rectSelectionHandler'
import { BrushSelectionHandler } from './types/brush/brushSelectionHandler'
import { TripodSelectionHandler } from './types/tripod/tripodSelectionHandler'
import { EntityFactory } from './factory/entityFactory'
import { createPointShape } from './types/point/pointEntity'
import { createRectShape } from './types/rect/rectEntity'
import { createDirectionShape } from './types/direction/directionEntity'
import { createBrushShape } from './types/brush/brushEntity'
import { createTripodShape } from './types/tripod/tripodEntity'

// 엔진(EntityController)의 private 집합 — 인터페이스 밖이라 구조 타입으로 읽는다(grabController와 같은 방식)
type EntityIdSets = { disabledEntityIds?: Set<string>; hiddenEntityIds?: Set<string> }
const entityIdSets = (engine: ICanvasEngineEntityController): EntityIdSets =>
  engine as unknown as EntityIdSets

/**
 * @module CanvasEngineEntity
 *
 * @description
 * CanvasEngineEntity는 도형을 생성하고 관리하는 클래스입니다.
 * 아래의 기능들을 포함합니다:
 * - 도형의 생성(Create)
 * - 도형의 내보내기(Export)
 * - 도형의 불러오기(Import)
 *
 * 또한, 해당 클래스는 다음의 기능을 제공하는 다른 확장 클래스들을 상속받습니다:
 * - SelectionHandler: 도형 선택 기능 제공
 * - BalloonExtension: 풍선(툴팁) 기능 제공
 *
 * 참조 순서는 다음과 같습니다.
 * CanvasEngineEntity -> BaseSelectionHandler -> BaseBalloonExtension -> BaseEntityProperties
 *
 *
 * @remarks
 * 이 모듈은 CanvasEngine의 핵심 엔티티들을 다루며, 도형 관련 기능을 통합 관리합니다.
 * 필요에 따라 추가적인 도형 기능이나 이벤트 핸들링 로직을 포함할 수 있습니다.
 */
export class CanvasEngineEntity extends BaseSelectionHandler {
  private __createShapeStyle: { shapeType?: 'line' | 'dash' | 'cloud'; isFill?: boolean } = {}

  public static importFromExportData(
    parent: ICanvasEngineEntityController,
    importData: CanvasEngineExportEntityType,
    interactionMode: boolean = true,
  ): CanvasEngineEntity {
    try {
      // EntityFactory를 사용하여 Entity 생성
      const factory = new EntityFactory(parent)
      const entity = factory.createEntityFromExportData(importData, interactionMode)

      // DIRECTION 타입의 경우 추가 로직 실행
      if (importData.type === SHAPES.DIRECTION) {
        // 방향도형은 points를 상대좌표에서 절대좌표로 변환
        const absolutePoints = importData.points
          ? importData.points.map((p, i) =>
              i % 2 === 0 ? p + importData.point.x : p + importData.point.y,
            )
          : undefined

        // 방향도형 불러오기
        if (absolutePoints && absolutePoints.length >= 4 && entity.__directionShape) {
          updateDirectionElements(
            entity.renderer,
            entity.__directionShape,
            absolutePoints,
            importData.color,
            1 * parent.connectedLineWidth,
            entity.calculatedArrowHeadSize,
            null,
            entity.getDirectionBalloonClip(),
          )
        }
      }

      return entity
    } catch (error) {
      console.error('Error importing entity:', error)
      throw error
    }
  }

  constructor(
    parent: ICanvasEngineEntityController,
    initConfig: {
      id?: string
      type: SHAPES
      createPoint: { x: number; y: number }
      balloonPoint?: { x: number; y: number }
      createConfig: ShapeCreateConfigs[SHAPES]
      rotation?: number
      layerId?: string
      points?: number[]
      strokeWidth?: number
      stroke?: string
      // 브러시 데이터
      brushLines?: Array<{
        points: number[]
        strokeWidth: number
        stroke: string
      }>
      isFill?: boolean
      shapeType?: 'line' | 'dash' | 'cloud'
      dotType?: 'dot' | 'arrow'
      balloonFillColor?: string
      balloonFontColor?: string
      balloonScale?: number
      connectedLineThicknessScale?: number
      balloonFontScale?: number
      pointShapeScale?: number
      areaBorderThicknessScale?: number
      interactionMode?: boolean
      isChanged?: boolean
      isShowTag?: boolean
      balloonTag?: { top: string; bottom: string }
    },
  ) {
    super(parent, undefined)
    this.canvasEngine = parent

    // 생성 시 지정된 테두리 스타일·채우기 — 색상 변경 뒤 재적용(getShapeStyleAttrs)에 쓴다.
    // (상속 시절에는 Konva attrs에 개별 설정 10종을 저장했으나 읽는 곳은 이 둘뿐이었다)
    this.__createShapeStyle = { shapeType: initConfig.shapeType, isFill: initConfig.isFill }

    const {
      id,
      type,
      stroke,
      createPoint,
      balloonPoint,
      createConfig,
      rotation,
      layerId,
      points,
      strokeWidth,
      interactionMode,
    } = initConfig

    const targetLayerId = layerId ?? this.canvasEngine.getDefaultLayerId()

    this.node.x(createPoint.x)
    this.node.y(createPoint.y)

    this.entityType = type

    switch (type) {
      case SHAPES.POINT: {
        const pointShapeData = createPointShape(this.renderer, this.canvasEngine, {
          id: initConfig.id,
          createPoint,
          balloonPoint,
          config: createConfig as ShapeCreateConfigs[typeof type],
          layerId: targetLayerId,
          pointShapeScale: initConfig.pointShapeScale,
          balloonFontColor: initConfig.balloonFontColor,
          balloonFillColor: initConfig.balloonFillColor,
          balloonScale: initConfig.balloonScale,
          connectedLineThicknessScale: initConfig.connectedLineThicknessScale,
          balloonFontScale: initConfig.balloonFontScale,
          areaBorderThicknessScale: initConfig.areaBorderThicknessScale,
          interactionMode,
        })
        this.__shape = pointShapeData.shape
        this.node.add(this.__shape)
        break
      }
      case SHAPES.RECT: {
        const rectShapeData = createRectShape(this.renderer, this.canvasEngine, {
          id: initConfig.id,
          createPoint,
          balloonPoint,
          config: createConfig as ShapeCreateConfigs[typeof type],
          rotation: initConfig.rotation,
          layerId: targetLayerId,
          isFill: initConfig.isFill,
          shapeType: initConfig.shapeType,
          balloonFontColor: initConfig.balloonFontColor,
          balloonFillColor: initConfig.balloonFillColor,
          balloonScale: initConfig.balloonScale,
          connectedLineThicknessScale: initConfig.connectedLineThicknessScale,
          balloonFontScale: initConfig.balloonFontScale,
          pointShapeScale: initConfig.pointShapeScale,
          areaBorderThicknessScale: initConfig.areaBorderThicknessScale,
          interactionMode,
        })
        this.__shape = rectShapeData.shape
        this.__shape_wrapper = rectShapeData.shapeWrapper
        this.node.add(this.__shape_wrapper)
        break
      }
      case SHAPES.DIRECTION: {
        const directionShapeData = createDirectionShape(this.renderer, this.canvasEngine, {
          id: initConfig.id,
          createPoint,
          balloonPoint,
          config: createConfig as ShapeCreateConfigs[typeof type],
          points,
          layerId: targetLayerId,
          balloonFontColor: initConfig.balloonFontColor,
          balloonFillColor: initConfig.balloonFillColor,
          balloonScale: initConfig.balloonScale,
          connectedLineThicknessScale: initConfig.connectedLineThicknessScale,
          balloonFontScale: initConfig.balloonFontScale,
          pointShapeScale: initConfig.pointShapeScale,
          areaBorderThicknessScale: initConfig.areaBorderThicknessScale,
          interactionMode,
        })
        this.node.add(directionShapeData.directionShape)
        this.node.add(directionShapeData.centerPoint)
        this.__shape = directionShapeData.centerPoint
        this.__directionShape = directionShapeData.directionShape
        this.__directionPoints = directionShapeData.directionPoints
        break
      }
      case SHAPES.BRUSH: {
        this.node.x(0)
        this.node.y(0)

        const brushShapeData = createBrushShape(this.renderer, {
          id: initConfig.id,
          createPoint,
          balloonPoint,
          config: createConfig as ShapeCreateConfigs[typeof type],
          points,
          rotation: initConfig.rotation,
          layerId: targetLayerId,
          brushLines: initConfig.brushLines,
          interactionMode,
        })
        this.__brush = brushShapeData.brush
        this.node.add(brushShapeData.brush)
        break
      }
      case SHAPES.TRIPOD: {
        // 화살표헤드-선 최소길이 임계 (줌레벨 보정, 선택 렌더의 방향 계산과 동일 공식)
        const tripodScale = this.renderer.stage.scaleX() || 1
        const tripodArrowMinLength =
          TRIPOD_ARROW_MIN_LENGTH *
          (1 + (TRIPOD_ARROW_MIN_LENGTH_FACTOR - 1) * (1 / tripodScale - 1))
        const tripodShapeData = createTripodShape(
          this.renderer,
          {
            id: initConfig.id,
            createPoint,
            balloonPoint,
            config: createConfig as ShapeCreateConfigs[typeof type],
            rotation: initConfig.rotation,
            layerId: targetLayerId,
            balloonFontColor: initConfig.balloonFontColor,
            balloonFillColor: initConfig.balloonFillColor,
            balloonScale: initConfig.balloonScale,
            connectedLineThicknessScale: initConfig.connectedLineThicknessScale,
            balloonFontScale: initConfig.balloonFontScale,
            pointShapeScale: initConfig.pointShapeScale,
            areaBorderThicknessScale: initConfig.areaBorderThicknessScale,
            interactionMode,
          },
          createPoint,
          this.calculatedConnectedLineWidth,
          this.canvasEngine.pointShapeScale,
          tripodArrowMinLength,
        )
        this.node.add(tripodShapeData.tripodTransformGroup)

        this.__shape = tripodShapeData.mainAxisLine
        this.__shape_wrapper = tripodShapeData.tripodGroup
        this.__tripod_transform_group = tripodShapeData.tripodTransformGroup
        this.node.add(this.__shape_wrapper)

        // TRIPOD 정보 저장
        this.setTripodShapePoints(tripodShapeData.pointData)

        break
      }
    }

    this.canvasEngine.pushToEntities(this)

    if (id) {
      this.setId(id)
      this.__id = id
    }
    if (rotation) this.setRotate(rotation)
    if (points && !this.isBrush() && !this.isDirection()) this.setPoints(points)
    if (stroke && !this.isBrush()) this.setStroke(stroke)
    if (strokeWidth && !this.isBrush()) this.setStrokeWidth(strokeWidth)

    // 개별 도형 스타일 설정 적용
    if (initConfig.shapeType !== undefined) {
      this.setIndividualShapeTypeSetting(initConfig.shapeType)
    }

    // 개별 점 도형 스타일 설정 적용
    if (initConfig.dotType !== undefined) {
      this.setIndividualDotTypeSetting(initConfig.dotType)
    }

    // 개별 채우기 설정 적용
    if (initConfig.isFill !== undefined) {
      this.setIndividualFillSetting(initConfig.isFill)
    }

    // 개별 벌룬 색상 설정 적용
    if (initConfig.balloonFontColor !== undefined) {
      this.setIndividualBalloonFontColor(initConfig.balloonFontColor)
    }
    if (initConfig.balloonFillColor !== undefined) {
      this.setIndividualBalloonFillColor(initConfig.balloonFillColor)
    }

    if (initConfig.balloonScale !== undefined) {
      this.setBalloonScale(initConfig.balloonScale)
    }
    if (initConfig.connectedLineThicknessScale !== undefined) {
      this.setConnectedLineThicknessScale(initConfig.connectedLineThicknessScale)
    }
    if (initConfig.balloonFontScale !== undefined) {
      this.setBalloonFontScale(initConfig.balloonFontScale)
    }
    if (initConfig.pointShapeScale !== undefined) {
      this.setPointShapeScale(initConfig.pointShapeScale)
    }
    if (initConfig.areaBorderThicknessScale !== undefined) {
      this.setAreaBorderThicknessScale(initConfig.areaBorderThicknessScale)
    }

    if (targetLayerId == parent.getBackgroundLayerId()) this.isBackgroundEntity = true

    if (this.entityType != SHAPES.BRUSH && !this.isBackgroundEntity) {
      if (this.entityType === SHAPES.DIRECTION) {
        // 끝점을 벌룬 중심점으로 사용
        if (points && points.length >= 4) {
          const endX = points[points.length - 2] // 마지막 x 좌표 (절대좌표)
          const endY = points[points.length - 1] // 마지막 y 좌표 (절대좌표)
          // balloonPoint를 우선사용 (불러올때는 balloonPoint가 있고 생성때는 끝점을 씀)
          const balloonPosition = balloonPoint ?? { x: endX, y: endY }
          const isExistingShape = balloonPoint !== undefined
          this.addBalloon(
            createConfig.color,
            initConfig.balloonFontColor!,
            initConfig.balloonFillColor!,
            '',
            balloonPosition,
            interactionMode,
            isExistingShape,
          )
          // 벌룬 생성 후 벌룬과 겹치는 선분 구간을 잘라내도록 재렌더
          this.setAreaLineWidthDirection()
        }
      } else if (this.entityType === SHAPES.TRIPOD) {
        // TRIPOD balloon 생성 (절대좌표를 전달하면 내부에서 상대좌표로 변환)
        const tripodBalloonPosition = balloonPoint ?? { x: 0, y: 0 }
        const isExistingShape = balloonPoint !== undefined
        this.addBalloon(
          createConfig.color,
          initConfig.balloonFontColor!,
          initConfig.balloonFillColor!,
          '',
          tripodBalloonPosition,
          interactionMode,
          isExistingShape,
        )
      } else {
        // 기존 도형들의 벌룬 생성
        const isExistingShape = balloonPoint !== undefined
        this.addBalloon(
          createConfig.color,
          initConfig.balloonFontColor!,
          initConfig.balloonFillColor!,
          '',
          balloonPoint ?? (this.isLine() ? { x: createPoint.x, y: createPoint.y } : { x: 0, y: 0 }),
          interactionMode,
          isExistingShape,
        )
      }
    }

    // this.on(CANVAS_ENTITY_EVENT.ENTITY_DRAG_START, () => {
    //   this.clearCache()
    // })

    // 개별 설정이 있는 경우 스타일 즉시 적용
    if (this.isRect() && initConfig.shapeType !== undefined) {
      this.setShapeStyleToggle(initConfig.shapeType)
    }
    if (this.isPoint() && initConfig.dotType !== undefined) {
      // balloon 생성 전이면 geometry는 placeholder. 이후 updateBalloonPosition에서 갱신됨.
      this.setDotStyleToggle(initConfig.dotType)
    }
    if ((this.isRect() || this.isEllipse()) && initConfig.isFill !== undefined) {
      this.setShapeFillToggle(initConfig.isFill)
    }

    if (initConfig.balloonFontColor !== undefined) {
      this.setIndividualBalloonFontColor(initConfig.balloonFontColor)
    }
    if (initConfig.balloonFillColor !== undefined) {
      this.setIndividualBalloonFillColor(initConfig.balloonFillColor)
    }
    if (initConfig.balloonScale !== undefined) {
      this.setBalloonScale(initConfig.balloonScale)
    }
    if (initConfig.connectedLineThicknessScale !== undefined) {
      this.setConnectedLineThicknessScale(initConfig.connectedLineThicknessScale)
    }
    if (initConfig.balloonFontScale !== undefined) {
      this.setBalloonFontScale(initConfig.balloonFontScale)
    }
    if (initConfig.pointShapeScale !== undefined) {
      this.setPointShapeScale(initConfig.pointShapeScale)
    }
    if (initConfig.areaBorderThicknessScale !== undefined) {
      this.setAreaBorderThicknessScale(initConfig.areaBorderThicknessScale)
    }

    // 엔티티에 마우스가 들어오면 커서를 클릭가능한 커서로 변경

    if (!isApp()) {
      this.avaliableShape.on(CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_ENTER, (e: IPointerEvent) =>
        this.mouseHover(e.evt as MouseEvent | TouchEvent),
      )
      this.avaliableShape.on(CANVAS_ENTITY_EVENT.ENTITY_FOCUS_MOUSE_LEAVE, (e: IPointerEvent) =>
        this.mouseLeave(e.evt as MouseEvent | TouchEvent),
      )
    }
    this.avaliableShape.on(
      `${CANVAS_ENTITY_EVENT.ENTITIY_FOCUS_MOUSE_DOWN} ${CANVAS_ENTITY_EVENT.ENTITIY_FOCUS_TOUCH_START}`,
      (e: IPointerEvent) => {
        if (!this.isSelected) return
        if (e.evt instanceof MouseEvent) {
          if (e.evt.button != 0) {
            this.disableShapeDrag()
            return
          }
        } else if (e.evt instanceof TouchEvent) {
          if (e.evt.touches.length > 1) return
        }
        this.enableShapeDrag()
      },
    )

    this.moveToLayer(targetLayerId)

    this.node.on(
      `${CANVAS_ENTITY_EVENT.SELECT_MOUSE_UP} ${CANVAS_ENTITY_EVENT.SELECT_TOUCH_END} ${CANVAS_ENTITY_EVENT.SELECT_TAP} ${CANVAS_ENTITY_EVENT.SELECT_CLICK}`,
      (e: IPointerEvent) => {
        // 드래그(스테이지 팬·도형 이동) 직후의 mouseup/touchend는 선택으로 취급하지 않음
        // — Konva는 드래그 후 tap/click은 억제하지만 touchend/mouseup은 손 뗀 지점의 도형에 발화하므로,
        //   패닝을 끝낸 지점(또는 낡은 hit 기준)의 결함이 선택되던 문제를 막는다
        if (this.renderer.justDragged()) {
          return
        }
        // 핀치 줌 상태 확인 - 핸들 컨트롤러의 핀치 줌 상태 확인
        if (this.canvasEngine.isPinchZooming) {
          return
        }
        // CREATE 모드일 때는 entity 선택 이벤트 중단 (CREATE가 우선)
        if (this.canvasEngine.isEntityCreating) {
          return
        }
        // listening이 false이거나 비활성화된 entity는 선택 불가
        if (
          !this.node.listening() ||
          entityIdSets(this.canvasEngine).disabledEntityIds?.has(this.getId()) ||
          entityIdSets(this.canvasEngine).hiddenEntityIds?.has(this.getId())
        ) {
          return
        }
        // ctrl+클릭: 다중 선택 전환 (click만 처리, grab 모드에서는 stage 핸들러가 처리)
        const isTouchEvt = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
        if (
          e.evt &&
          !isTouchEvt &&
          ((e.evt as MouseEvent).ctrlKey || (e.evt as MouseEvent).metaKey)
        ) {
          if (e.type !== 'click') return
          setTimeout(() => this.canvasEngine.ctrlSelectEntity(this), 0)
          return
        }
        this.canvasEngine.selectShape(this)
      },
    )

    this.changeColor(createConfig.color)

    // SelectionStrategy 주입 (타입별 선택 핸들러)
    this.setSelectionStrategy(this.createSelectionStrategy(type))
  }

  /**
   * 타입에 맞는 SelectionStrategy 생성
   * @param type - Entity 타입
   * @returns 타입별 SelectionStrategy 인스턴스
   */
  private createSelectionStrategy(type: SHAPES): SelectionStrategy {
    switch (type) {
      case SHAPES.POINT:
        return new PointSelectionHandler()
      case SHAPES.DIRECTION:
        return new DirectionSelectionHandler()
      case SHAPES.RECT:
        return new RectSelectionHandler()
      case SHAPES.BRUSH:
        return new BrushSelectionHandler()
      case SHAPES.TRIPOD:
        return new TripodSelectionHandler()
      default:
        // 기본값으로 RectSelectionHandler 사용
        return new RectSelectionHandler()
    }
  }

  moveToLayer(layerId: string): void {
    this.__layerId = layerId
    this.canvasEngine.moveEntityToLayer(this, layerId)
  }

  // 방향도형을 이동시키는 함수
  public moveDirection(dx: number, dy: number): void {
    if (!this.isDirection() || !this.__directionPoints) return

    // 모든 points 좌표 업데이트
    const newPoints = this.__directionPoints.map((p: number, i: number) =>
      i % 2 === 0 ? p + dx : p + dy,
    )
    this.__directionPoints = newPoints

    // 다시 그리기
    if (this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        newPoints,
        this.__color,
        1 * this.calculatedConnectedLineWidth,
        this.calculatedArrowHeadSize,
        null,
        this.getDirectionBalloonClip(),
      )
    }

    // 벌룬 위치 업데이트
    if (this.balloon) {
      const currentPos = this.balloon.getPosition()
      this.balloon.setPosition({ x: currentPos.x + dx, y: currentPos.y + dy })
      this.updateTextPosition()
    }

    // 본도형 위치는 0,0으로 유지

    this.node.getLayer()?.batchDraw()
  }

  // 방향도형의 회전 함수 (도형자체를 돌리지 않고 좌표를 계산해서 돌림)
  public rotateDirection(angle: number, origin: { x: number; y: number }): void {
    if (!this.isDirection() || !this.__directionPoints) return

    const angleRad = (angle * Math.PI) / 180
    const cos = Math.cos(angleRad)
    const sin = Math.sin(angleRad)

    const rotatePoint = (p: { x: number; y: number }): Vector2d => {
      const dx = p.x - origin.x
      const dy = p.y - origin.y
      return {
        x: dx * cos - dy * sin + origin.x,
        y: dx * sin + dy * cos + origin.y,
      }
    }

    // points 좌표 회전
    const newPoints: number[] = []
    for (let i = 0; i < this.__directionPoints.length; i += 2) {
      const rotated = rotatePoint({
        x: this.__directionPoints[i],
        y: this.__directionPoints[i + 1],
      })
      newPoints.push(rotated.x, rotated.y)
    }
    this.__directionPoints = newPoints

    // 벌룬 위치
    if (this.balloon) {
      const currentPos = this.balloon.getPosition()
      const rotatedBalloonPos = rotatePoint(currentPos)
      this.balloon.setPosition(rotatedBalloonPos)
      this.updateTextPosition()
    }

    // 핸들 위치
    if (this.__shape) {
      const rotatedHandlePos = rotatePoint(this.__shape.position())
      this.__shape.position(rotatedHandlePos)
    }

    // 다시 그리기
    if (this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        this.__directionPoints,
        this.__color,
        1 * this.calculatedConnectedLineWidth,
        this.calculatedArrowHeadSize,
        null,
        this.getDirectionBalloonClip(),
      )
    }

    this.node.getLayer()?.batchDraw()
  }

  // 방향도형의 경로를 업데이트하는 함수
  public updateDirectionPath(points: number[]): void {
    if (!this.isDirection()) return
    if (!points || points.length < 4) return
    if (points.length % 2 !== 0) return // x,y 좌표 쌍이어야 함

    // __directionPoints 업데이트
    this.__directionPoints = [...points]

    // 다시 그리기
    if (this.__directionShape) {
      updateDirectionElements(
        this.renderer,
        this.__directionShape,
        this.__directionPoints,
        this.__color,
        1 * this.calculatedConnectedLineWidth,
        this.calculatedArrowHeadSize,
        null,
        this.getDirectionBalloonClip(),
      )
    }

    // 핸들 위치 업데이트 (첫 번째 점으로)
    if (this.__shape) {
      this.__shape.position({
        x: points[0],
        y: points[1],
      })
    }

    // 벌룬 위치 업데이트 (마지막 점으로)
    if (this.balloon) {
      const endX = points[points.length - 2]
      const endY = points[points.length - 1]
      this.moveBalloonToGlobalPosition({ x: endX, y: endY })
    }

    this.node.getLayer()?.batchDraw()
  }

  toExportData(): CanvasEngineExportEntityType {
    const _exportObject: CanvasEngineExportEntityType = {
      layerId: this.getLayerId(),
      shapeId: this.getId(),
      color: '',
      point: { x: 0, y: 0 },
      points: [],
      rotate: 0,
      type: SHAPES.POINT,
      balloonPoint: { x: 0, y: 0 },
      width: 0,
      height: 0,
      template: this.__template,
    }

    _exportObject.type = this.entityType
    if (this.entityType === SHAPES.LINE) {
      _exportObject.points = this.getPoints()
      _exportObject.color = this.getStroke()?.toString() ?? ''
    } else if (this.entityType === SHAPES.BRUSH) {
      if (this.__brush) {
        // 브러시 그룹에서 모든 실제 브러시 Line 찾기
        const allLines: ILine[] = this.__brush.find('Line')
        const brushLines = allLines.filter(
          (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
        )

        if (brushLines.length > 0) {
          const firstLine = brushLines[0]
          const firstPoints = firstLine.points()

          // 첫 번째 Line의 첫 번째 점을 기준점으로 사용
          const exportPosition =
            firstPoints.length >= 2 ? { x: firstPoints[0], y: firstPoints[1] } : { x: 0, y: 0 }

          _exportObject.point = exportPosition
          _exportObject.rotate = this.getRotate()
          _exportObject.width = firstLine.strokeWidth() ?? 1
          _exportObject.color = firstLine.stroke()?.toString() ?? ''

          // 모든 브러시 Line 데이터 수집
          const allBrushData: Array<{
            points: number[]
            strokeWidth: number
            stroke: string
          }> = []

          for (const line of brushLines) {
            allBrushData.push({
              points: [...line.points()],
              strokeWidth: line.strokeWidth() ?? 1,
              stroke: line.stroke()?.toString() || '',
            })
          }

          _exportObject.brushLines = allBrushData
          _exportObject.points = [] // brushLines 사용 시 points는 비움
        }
      }
    } else if (this.entityType === SHAPES.DIRECTION) {
      _exportObject.point = { x: 0, y: 0 } // 방향도형의 기준점은 항상 0,0
      _exportObject.points = this.getPoints() // points는 절대좌표
      _exportObject.color = this.getStroke()?.toString() ?? ''
      _exportObject.rotate = this.getRotate()
    } else if (this.entityType === SHAPES.TRIPOD) {
      // TRIPOD 도형 export
      _exportObject.point = { x: this.node.x(), y: this.node.y() }
      _exportObject.color = this.getStroke()?.toString() ?? ''
      _exportObject.rotate = this.getRotate()
      _exportObject.width = this.getShapeWidth()
      _exportObject.height = this.getShapeHeight()

      // TRIPOD 고유 데이터 추가
      if (this.getTripodShapePoints()) {
        _exportObject.tripodShapePoints = this.getTripodShapePoints()
      }
    } else {
      // 점·사각 등 나머지 (상속 시절 조건 `this instanceof Konva.Group`은 항상 참이었다)
      _exportObject.point = { x: this.node.x(), y: this.node.y() }
      _exportObject.color = this.getFill()
        ? this.getFill().toString()
        : (this.getStroke()?.toString() ?? '')
      _exportObject.rotate = this.getRotate()
      // 흡수되지 않은 잔존 scale 반영 — 멀티터치로 transform이 중단되면 scale이 남은 채
      // export될 수 있어, scale 미반영 시 화면과 다른(축소된) 크기가 저장됨.
      // scale 0(비정상 프레임)은 1로 취급해 크기 0 저장을 방지.
      const shapeScaleX = this.__shape ? Math.abs(this.__shape.scaleX() || 1) : 1
      const shapeScaleY = this.__shape ? Math.abs(this.__shape.scaleY() || 1) : 1
      _exportObject.width = this.getShapeWidth() * shapeScaleX
      _exportObject.height = this.getShapeHeight() * shapeScaleY
    }

    const ballon = this.balloon?.head.rect
    if (ballon) {
      if (this.isLine()) {
        const points = this.getPoints()
        _exportObject.balloonPoint = { x: ballon.x() - points[0], y: ballon.y() - points[1] }
      } else if (this.isDirection()) {
        // 방향도형은 실제 벌룬 위치를 절대좌표로 저장
        _exportObject.balloonPoint = {
          x: ballon.x(),
          y: ballon.y(),
        }
      } else if (this.isTripod()) {
        // TRIPOD도 실제 벌룬 위치를 절대좌표로 저장
        _exportObject.balloonPoint = {
          x: ballon.x(),
          y: ballon.y(),
        }
      } else {
        _exportObject.balloonPoint = { x: ballon.x(), y: ballon.y() }
      }
    }

    // 개별 설정들 추가
    if (this.hasIndividualFillSetting()) {
      _exportObject.isFill = this.getIndividualFillSetting()
    }

    if (this.hasIndividualShapeTypeSetting()) {
      _exportObject.rectType = this.getIndividualShapeTypeSetting()
    }

    // POINT 화살표 모드 export
    if (this.isPoint() && this.hasIndividualDotTypeSetting()) {
      _exportObject.dotType = this.getIndividualDotTypeSetting()
    }

    // Balloon 색상: 개별 설정이 있으면 사용, 없으면 전역 설정 사용
    if (this.hasIndividualBalloonFontColor()) {
      _exportObject.balloonFontColor = this.getIndividualBalloonFontColor()
    } else if (this.canvasEngine.globalBalloonFontColor) {
      _exportObject.balloonFontColor = this.canvasEngine.globalBalloonFontColor
    }

    if (this.hasIndividualBalloonFillColor()) {
      _exportObject.balloonFillColor = this.getIndividualBalloonFillColor()
    } else if (this.canvasEngine.globalBalloonFillColor) {
      _exportObject.balloonFillColor = this.canvasEngine.globalBalloonFillColor
    }

    if (this.hasIndividualBalloonScale()) {
      _exportObject.balloonScale = this.getIndividualBalloonScale()
    }
    if (this.hasIndividualConnectedLineThicknessScale()) {
      _exportObject.connectedLineThicknessScale = this.getIndividualConnectedLineThicknessScale()
    }
    if (this.hasIndividualBalloonFontScale()) {
      _exportObject.balloonFontScale = this.getIndividualBalloonFontScale()
    }
    if (this.hasIndividualPointShapeScale()) {
      _exportObject.pointShapeScale = this.getIndividualPointShapeScale()
    }
    if (this.hasIndividualAreaBorderThicknessScale()) {
      _exportObject.areaBorderThicknessScale = this.getIndividualAreaBorderThicknessScale()
    }

    // TRIPOD tailStyle 개별 설정 저장
    if (this.isTripod() && this.hasIndividualTailStyle()) {
      const tripodShapePoints = this.getTripodShapePoints()
      const individualTailStyle = this.getIndividualTailStyle()
      if (tripodShapePoints && individualTailStyle) {
        tripodShapePoints.tailStyle = individualTailStyle
        _exportObject.tripodShapePoints = tripodShapePoints
      }
    }

    return _exportObject
  }

  getLayerId(): string {
    return this.__layerId
  }

  /** 생성 시 지정된 테두리 스타일·채우기. 색상 변경 뒤 재적용용(미지정이면 undefined 그대로 — 상속 시절 attrs와 동일) */
  getShapeStyleAttrs(): { shapeType: 'line' | 'dash' | 'cloud'; isFill: boolean } {
    return this.__createShapeStyle as { shapeType: 'line' | 'dash' | 'cloud'; isFill: boolean }
  }

  destroyFromEngine(): void {
    // 선택 상태인 경우 먼저 blur 처리하여 transformer와 이벤트 정리
    if (this.isSelected) {
      this.blur()
    }

    if (this.__line && !this.isBrush()) this.__line.destroy()
    if (this.__brush) this.__brush.destroy()
    if (this.__shape) this.__shape.destroy()
    if (this.balloon) {
      this.balloon.destroy()
    }
    this.canvasEngine.removeEntity(this.getId())

    this.node.destroy()
  }

  getBalloonScale(): number {
    return this.getIndividualBalloonScale()
  }
  getConnectedLineThicknessScale(): number {
    return this.getIndividualConnectedLineThicknessScale()
  }
  getBalloonFontScale(): number {
    return this.getIndividualBalloonFontScale()
  }
  getPointShapeScale(): number {
    return this.getIndividualPointShapeScale()
  }
  getAreaBorderThicknessScale(): number {
    return this.getIndividualAreaBorderThicknessScale()
  }

  applyShapeScale(scale: number): void {
    // 벌룬에만 적용 (크기와 테두리 굵기)
    this.applyBalloonScale(scale)
  }
  applyConnectedLineSize(): void {
    if (this.balloon) {
      this.balloon.connectedLine.line.strokeWidth(this.canvasEngine.connectedLineWidth)
    }

    // TRIPOD의 경우 내부 연결선들도 업데이트
    if (this.entityType === SHAPES.TRIPOD && this.__shape_wrapper) {
      const newStrokeWidth = this.calculatedConnectedLineWidth
      const tripodGroup = this.__tripod_transform_group
      if (tripodGroup) {
        tripodGroup.getChildren((node: INode) => {
          if (isLineNode(node)) {
            // 모든 Line 노드 (mainAxis 및 connection lines)의 strokeWidth 업데이트
            node.strokeWidth(newStrokeWidth)
          }
          return false // 모든 노드를 검사
        })
      }
    }
  }
  applyBalloonFontSize(): void {
    if (this.balloon) {
      const fontSize = this.canvasEngine.balloonFontSize
      if (fontSize === 0) {
        this.balloon.head.text.visible(false)
      } else {
        this.balloon.head.text.visible(true)
        this.balloon.head.text.fontSize(fontSize)
      }
      this.updateTextPosition()
    }
  }
  applyPointSize(): void {
    if (this.entityType === SHAPES.POINT && this.__shape) {
      const newPointSize = this.getIndividualPointShapeScale() ?? this.canvasEngine.pointShapeScale
      // 포인트 모양이 Circle인 경우
      if (isEllipseNode(this.__shape)) {
        this.__shape.radiusX(newPointSize)
        this.__shape.radiusY(newPointSize)
        // 히트 영역도 새로운 크기로 업데이트 (생성 시 ×8과 달리 ×2 — 기존 동작 보존)
        this.__shape.hitArea({ kind: 'circle', radius: newPointSize * 2 })
      }
    } else if (this.entityType === SHAPES.TRIPOD && this.__tripod_transform_group) {
      // TRIPOD의 시작점들 크기 업데이트
      const newPointSize = this.canvasEngine.balloonSize
      const mainAxisHitWidth = 20
      const startPointHitWidth = Math.round(mainAxisHitWidth * 1.2)
      // tripodGroup 안의 Circle 노드들을 타입별로 업데이트
      this.__tripod_transform_group.getChildren((node: INode) => {
        if (isEllipseNode(node)) {
          if (node.name() === 'startPointVisual') {
            // visual circle: 표준 포인트 크기로 업데이트
            node.radiusX(newPointSize)
            node.radiusY(newPointSize)
          } else if (node.name() === 'startPoint') {
            // hit circle: 주축선 hit width의 1.2배 반지름으로 업데이트
            const hitRadius = Math.max(newPointSize + 6, startPointHitWidth / 2 + 2)
            node.radiusX(hitRadius)
            node.radiusY(hitRadius)
          }
        }
        return false // 모든 노드를 검사
      })
    }
  }
  applyAreaBorderThickness(): void {
    if (this.entityType === SHAPES.TRIPOD && this.__shape_wrapper) {
      // TRIPOD의 주축선과 연결선들의 굵기 업데이트
      const newStrokeWidth = this.calculatedConnectedLineWidth
      const tripodGroup = this.__tripod_transform_group
      if (tripodGroup) {
        tripodGroup.getChildren((node: INode) => {
          if (isLineNode(node)) {
            // 모든 Line 노드 (mainAxis 및 connection lines)의 strokeWidth 업데이트
            node.strokeWidth(newStrokeWidth)
          }
          return false // 모든 노드를 검사
        })
      }
    } else if (
      this.entityType !== SHAPES.DIRECTION &&
      this.entityType !== SHAPES.TRIPOD &&
      this.entityType === SHAPES.RECT &&
      this.__shape
    ) {
      // shape의 테두리 굵기 설정 (balloon 테두리 제외)
      this.__shape.strokeWidth(this.canvasEngine.areaLineWidth)
    }
  }
}
